/**
 * Meditation Generation Hook with Global Job Tracking
 *
 * Wraps the meditationApi.generateMeditationAsync function with:
 * - Global job tracking (persists across page navigation)
 * - Progress callbacks
 * - Error handling
 *
 * This hook should be used instead of directly calling generateMeditationAsync
 * to ensure users get proper feedback about their session generation status.
 */

import { useCallback } from 'react';
import { useJobTracker, JobType } from '@/contexts/JobTrackerContext';
import { generateMeditationAsync, MeditationGenerationParams, MeditationGenerationResult } from '@/utils/meditationApi';

// Map form_type/category to JobType
const getJobType = (params: MeditationGenerationParams): JobType => {
  const formType = params.form_type?.toLowerCase() || '';
  const categoryId = params.categoryId?.toLowerCase() || params.category_id?.toLowerCase() || '';

  const typeMap: Record<string, JobType> = {
    meditation: 'meditation',
    zen: 'zen',
    affirmation: 'affirmation',
    sleep: 'sleep',
    'sleep-story': 'sleep',
  };

  // Check form_type first
  if (typeMap[formType]) {
    return typeMap[formType];
  }

  // Then check categoryId
  if (categoryId.includes('zen')) return 'zen';
  if (categoryId.includes('affirmation')) return 'affirmation';
  if (categoryId.includes('sleep')) return 'sleep';
  if (categoryId.includes('meditation')) return 'meditation';

  return 'meditation'; // Default to meditation
};

interface UseMeditationGenerationOptions {
  onProgress?: (progress: number, message: string) => void;
  onJobCreated?: (jobId: string) => void;
  maxWaitSeconds?: number;
  pollInterval?: number;
}

export const useMeditationGeneration = () => {
  const { trackJob } = useJobTracker();

  const generate = useCallback(async (
    params: MeditationGenerationParams,
    options?: UseMeditationGenerationOptions
  ): Promise<{ data: MeditationGenerationResult | null; error: any | null }> => {
    const jobType = getJobType(params);

    return generateMeditationAsync(params, {
      ...options,
      onJobCreated: (jobId: string) => {
        // Track job globally for persistent notifications
        trackJob(jobId, jobType);

        // Also call the original callback if provided
        if (options?.onJobCreated) {
          options.onJobCreated(jobId);
        }
      },
    });
  }, [trackJob]);

  return { generate };
};
